import { downloadCSV } from 'react-admin';
import type { MerchantResource } from '../../../../Entities/Merchant';
import type { MerchantStatusResource } from '../../../../Entities/MerchantStatus';
import type { MerchantsOfferSummariesResource } from '../../../../Entities/MerchantsOfferSummary';
import type { RegionResource } from '../../../../Entities/Region';
import { escapeCsv, type ReportFilter } from '../../../../Components/ReportsWidget/utils';
import { idFrom } from '../../../../utils';

export interface Filter extends ReportFilter {
  merchant_ids?: (string | number)[];
  has_offers?: boolean | null;
}

export interface PrefetchedData {
  merchants_offer_summaries?: MerchantsOfferSummariesResource[];
  merchants?: MerchantResource[];
  merchant_statuses?: MerchantStatusResource[];
  regions?: RegionResource[];
}

export function getMerchantOfferSummaryReportParams(filter: Filter) {
  return {
    regionIds: toNumberIds(filter.region_ids),
    merchantIds: toNumberIds(filter.merchant_ids),
    hasOffers: typeof filter.has_offers === 'boolean' ? filter.has_offers : undefined,
  };
}

export function getMerchantName(
  record: MerchantsOfferSummariesResource,
  prefetched: PrefetchedData,
): string {
  return (
    prefetched.merchants?.find((merchant) => merchant.id === idFrom(record, 'merchant'))?.attributes
      ?.merchant_name ?? ''
  );
}

export function downloadMerchantsOfferSummariesCsv(
  records: MerchantsOfferSummariesResource[],
  prefetched: PrefetchedData,
): void {
  const header = [
    'Merchant ID',
    'Merchant',
    'Region',
    'Offers',
    'Categorized',
    'Not Categorized',
    'Comparable',
    'Incomparable',
    'Combined',
    'Not Combined',
    'Approved',
    'Approved Categorized',
    'Approved Not Categorized',
    'Approved Categorized Comparable',
    'Approved Categorized Incomparable',
    'Approved Categorized Comparable Combined',
    'Approved Categorized Comparable Not Combined',
    'Disapproved',
    'Disapproved Categorized',
    'Disapproved Not Categorized',
    'Disapproved Categorized Comparable',
    'Disapproved Categorized Incomparable',
    'Disapproved Categorized Comparable Combined',
    'Disapproved Categorized Comparable Not Combined',
  ].join(',');

  const rows = records.map((record) => {
    const attrs = record.attributes ?? {};
    const merchant = prefetched.merchants?.find((m) => m.id === idFrom(record, 'merchant'));
    const region = prefetched.regions?.find((r) => r.id === idFrom(record, 'region'));

    return [
      record.relationships?.merchant?.data?.id ?? '',
      escapeCsv(merchant?.attributes?.merchant_name),
      escapeCsv(region?.attributes?.name),
      attrs.total_offers ?? '',
      attrs.categorized_offers ?? '',
      attrs.not_categorized_offers ?? '',
      attrs.comparable_offers ?? '',
      attrs.incomparable_offers ?? '',
      attrs.combined_offers ?? '',
      attrs.not_combined_offers ?? '',
      attrs.approved_offers ?? '',
      attrs.approved_categorized_offers ?? '',
      attrs.approved_not_categorized_offers ?? '',
      attrs.approved_categorized_comparable_offers ?? '',
      attrs.approved_categorized_incomparable_offers ?? '',
      attrs.approved_categorized_comparable_combined_offers ?? '',
      attrs.approved_categorized_comparable_not_combined_offers ?? '',
      attrs.disapproved_offers ?? '',
      attrs.disapproved_categorized_offers ?? '',
      attrs.disapproved_not_categorized_offers ?? '',
      attrs.disapproved_categorized_comparable_offers ?? '',
      attrs.disapproved_categorized_incomparable_offers ?? '',
      attrs.disapproved_categorized_comparable_combined_offers ?? '',
      attrs.disapproved_categorized_comparable_not_combined_offers ?? '',
    ].join(',');
  });

  downloadCSV([header, ...rows].join('\n'), 'merchants_offer_summary');
}

export function toNumberIds(values?: (string | number)[]): number[] | undefined {
  const ids = values
    ?.filter(
      (value) =>
        (typeof value === 'number' && Number.isFinite(value)) ||
        (typeof value === 'string' && /^\d+$/.test(value)),
    )
    .map(Number);
  return ids?.length ? ids : undefined;
}
